import MultipleChoicePreview from '../../QuestionPreviews/MultipleChoicePreview/MultipleChoicePreview';
import DraggableList from '../../../../../../../components/Draggable/DraggableList/DraggableList';
import { useDispatch } from 'react-redux';
import { Box, TextField, IconButton, Grid } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Droppable } from 'react-beautiful-dnd';
import { useMemo, useState, useEffect } from 'react';
import {
  addQuestionOption,
  removeQuestionOption,
  updateQuestionOption
} from '../../../../../../../store/actions/dataEntryTemplateGeneratorActions';
import './MultipleChoiceQuestion.css';

const MultipleChoiceQuestion = ({ question, onChangeAlert, templateType }) => {
  const { config, variant, options: questionOptions = [], isActive, id: questionId } = question;
  const dispatch = useDispatch();
  const [isMounted, setIsMounted] = useState(false);
  const [draggableOptions, setDraggableOptions] = useState([]);

  // Use a simple droppable ID to avoid React 18 warnings
  const droppableId = useMemo(() => `QUESTION_DROPPABLE_${questionId}`, [questionId]);

  // On React 18 we need to load Droppable element after the component is mounted
  // See: https://github.com/atlassian/react-beautiful-dnd/issues/2399
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleAddOption = () => {
    const payload = {
      questionId: question.id,
      option: {
        id: new Date().getTime(),
        value: '',
        option: '',
        order: questionOptions?.length + 1,
        error: {}
      }
    };

    dispatch(addQuestionOption(payload));
    onChangeAlert();
  };

  const handleRemoveOption = (option) => {
    dispatch(removeQuestionOption({ questionId: question.id, optionId: option?.id }));
    onChangeAlert();
  };

  const handleChangeOption = (event, optionId) => {
    const payload = {
      questionId: question.id,
      optionId,
      option: { [event.target.name]: event.target.value }
    };

    dispatch(updateQuestionOption(payload));
    onChangeAlert();
  };

  useEffect(() => {
    const setupItems = () => {
      if (questionOptions && questionOptions?.length > 0) {
        const safeOptions = [];
        questionOptions.forEach((option, index) => {
          const newOption = {
            originalOption: option,
            id: option?.id || index,
            itemId: option?.id,
            order: option?.order || index + 1,
            content: (
              <Grid className='multiple-choice-options__grid' container alignItems='center'>
                {/* Drag Handle */}
                <Grid
                  item
                  xs={0.5}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'grab',
                    '&:active': {
                      cursor: 'grabbing'
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon='fa-solid fa-grip-dots-vertical'
                    style={{ marginRight: '1rem', color: '#9CA3AF' }}
                  />
                </Grid>

                {/* Value Container with Icon */}
                <Grid className='multiple-choice-options__value-container' item xs={2.5}>
                  <FontAwesomeIcon
                    icon={['far', 'square']}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  />
                  {variant && variant?.value === 'numeric' ? (
                    <TextField
                      label='Value'
                      type='number'
                      name='value'
                      value={option?.value || ''}
                      error={!!(option?.error?.value && option?.error?.value !== '')}
                      helperText={option?.error?.value}
                      onChange={(event) => handleChangeOption(event, option?.id)}
                      style={{ mr: 1, width: '100%' }}
                      className='custom-text-field'
                      variant='standard'
                    />
                  ) : (
                    <TextField
                      label='Value'
                      name='value'
                      value={option?.value || ''}
                      error={!!(option?.error?.value && option?.error?.value !== '')}
                      helperText={option?.error?.value}
                      onChange={(event) => handleChangeOption(event, option?.id)}
                      style={{ mr: 1, width: '100%' }}
                      className='custom-text-field'
                      variant='standard'
                    />
                  )}
                </Grid>

                {/* Option Field */}
                <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    label='Option'
                    name='option'
                    value={option?.option || ''}
                    error={!!(option?.error?.option && option?.error?.option !== '')}
                    helperText={option?.error?.option}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, flex: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid className='multiple-choice-options__action' item xs={1}>
                  {questionOptions.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveOption(option)}
                      className='multiple-choice-options__action-button'
                      size='small'
                    >
                      <FontAwesomeIcon
                        icon='circle-minus'
                        className='multiple-choice-options__action-button__icon'
                      />
                    </IconButton>
                  )}
                  {(config?.maxOptions === -1 || questionOptions.length < config?.maxOptions) && (
                    <IconButton
                      onClick={handleAddOption}
                      className='multiple-choice-options__action-button'
                      size='small'
                    >
                      <FontAwesomeIcon icon='plus' className='multiple-choice-options__action-button__icon' />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            )
          };
          safeOptions.push(newOption);
        });
        setDraggableOptions(safeOptions);
      } else {
        setDraggableOptions([]);
      }
    };

    setupItems();
  }, [questionOptions]);

  // Don't render drag and drop if not active
  if (!isActive) {
    return <MultipleChoicePreview question={question} templateType={templateType} />;
  }

  return (
    <Box className='multiple-choice-options'>
      {!isMounted ? (
        // Static version for initial render
        <Box>
          {questionOptions.map((option, index) => (
            <Grid
              key={option?.id || `static-${index}`}
              className='multiple-choice-options__grid'
              container
              alignItems='center'
            >
              <Grid item xs={0.5}>
                <FontAwesomeIcon
                  icon='fa-solid fa-grip-dots-vertical'
                  style={{ marginRight: '1rem', color: '#9CA3AF' }}
                />
              </Grid>
              <Grid className='multiple-choice-options__value-container' item xs={2.5}>
                <FontAwesomeIcon
                  icon={['far', 'square']}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '10px',
                    flexShrink: 0
                  }}
                />
                {variant && variant?.value === 'numeric' ? (
                  <TextField
                    label='Value'
                    type='number'
                    name='value'
                    value={option?.value || ''}
                    error={!!(option?.error?.value && option?.error?.value !== '')}
                    helperText={option?.error?.value}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                ) : (
                  <TextField
                    label='Value'
                    name='value'
                    value={option?.value || ''}
                    error={!!(option?.error?.value && option?.error?.value !== '')}
                    helperText={option?.error?.value}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                )}
              </Grid>
              <Grid item xs={8} style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label='Option'
                  name='option'
                  value={option?.option || ''}
                  error={!!(option?.error?.option && option?.error?.option !== '')}
                  helperText={option?.error?.option}
                  onChange={(event) => handleChangeOption(event, option?.id)}
                  style={{ mr: 1, flex: 1, width: '100%' }}
                  className='custom-text-field'
                  variant='standard'
                />
              </Grid>
              <Grid className='multiple-choice-options__action' item xs={1}>
                {questionOptions.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveOption(option)}
                    className='multiple-choice-options__action-button'
                    size='small'
                  >
                    <FontAwesomeIcon
                      icon='circle-minus'
                      className='multiple-choice-options__action-button__icon'
                    />
                  </IconButton>
                )}
                {(config?.maxOptions === -1 || questionOptions.length < config?.maxOptions) && (
                  <IconButton
                    onClick={handleAddOption}
                    className='multiple-choice-options__action-button'
                    size='small'
                  >
                    <FontAwesomeIcon icon='plus' className='multiple-choice-options__action-button__icon' />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>
      ) : (
        // Draggable version after mount

        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`multiple-choice-options__container ${
                snapshot.isDraggingOver ? 'multiple-choice-options__container--dragging-over' : ''
              }`}
            >
              <DraggableList items={draggableOptions} isDragDisabled={false} />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </Box>
  );
};

export default MultipleChoiceQuestion;
